import { makeAutoObservable } from 'mobx';

import {
  DumCommandApi,
  DumDataHoverDto,
  DumGlobalEventDetailsDto,
  DumQueryApi,
  ModelError
} from '@src/Api/generated-global-event';

import { RootStore } from '@src/Store/Store';
import axios, { AxiosError, CancelTokenSource } from 'axios';
import { FilterDataTimeLine, monthLists } from './DUMEvents.const';
import {
  DumDTO,
  IDataTimeLine,
  IDumGlobalEventDetailHoverData,
  IDumSorted,
  IItemTimeLine,
  IMapCoordinate,
  IMapCoordinateGrouped,
  IReqBodyEvent,
  IStatusApi,
  defaultDataTimeLine,
  defaultStatusApi
} from './DUMEvents.type';

export type OverviewType = {
  year: number;
  months: { contain_mythic_event: boolean; month: number }[];
}[];

/**
 * Pulls the service's own rejection text out of the standard 400 envelope.
 * Banner rejections (size, format, magic-byte mismatch, upload failure) are only
 * described there; `error.message` alone is just "Request failed with status
 * code 400", which tells the user nothing actionable.
 */
const readServerErrorMessage = (error: unknown): string | undefined => {
  if (!axios.isAxiosError(error)) {
    return undefined;
  }
  const body = (error as AxiosError<ModelError>).response?.data;
  const message = body?.message;
  return typeof message === 'string' && message.trim() ? message : undefined;
};

const catchError = (err: unknown) => {
  if (err instanceof AxiosError) {
    if (err.response?.status !== 404) {
      let checkErr = 'Something went wrong';
      if (`${err}`.includes('undefined')) {
        checkErr =
          'Error, Something went wrong. Please refresh or try again later';
      }

      const newStatus = {
        isSuccess: false,
        isLoading: false,
        isError: true,
        message: `${checkErr}`
      };

      return {
        data: [],
        dataMaps: [],
        dataTimeLine: defaultDataTimeLine,
        status: newStatus,
        totalEventDUM: 0,
        totalEventCasefile: 0
      };
    }
    return {
      data: [],
      dataMaps: [],
      dataTimeLine: defaultDataTimeLine,
      status: {
        isSuccess: false,
        isLoading: false,
        isError: false,
        message: ''
      },
      totalEventDUM: 0,
      totalEventCasefile: 0
    };
  } else if (err instanceof Error) {
    let checkErr = 'Something went wrong';
    if (`${err}`.includes('undefined')) {
      checkErr =
        'Error, Something went wrong. Please refresh or try again later';
    }

    const newStatus = {
      isSuccess: false,
      isLoading: false,
      isError: true,
      message: `${checkErr}`
    };

    return {
      data: [],
      dataMaps: [],
      dataTimeLine: defaultDataTimeLine,
      status: newStatus,
      totalEventDUM: 0,
      totalEventCasefile: 0
    };
  }
};

const sortingFoundData = async (overviews: OverviewType) => {
  const sortedData = overviews;
  const newListData: IDumSorted[] = [];
  sortedData.sort((a, b) => a.year - b.year);
  for await (const elementOfYear of sortedData) {
    const monthlyData = elementOfYear.months;
    monthlyData.sort((a, b) => a.month - b.month);
    for await (const elementOfMonth of monthlyData) {
      const isMythicEvent = elementOfMonth.contain_mythic_event;
      const dataTimeLine = {
        title: monthLists[elementOfMonth.month - 1],
        description: elementOfYear.year,
        year: elementOfYear.year,
        month: Number(elementOfMonth.month.toString().padStart(2, '0')),
        isMythicEvent: isMythicEvent,
        events: []
      };
      newListData.push(dataTimeLine);
    }
  }
  return {
    newListData,
    sortedData
  };
};

export interface IDUMEventsStore {
  DUMEventDatas: {
    data: IDumSorted[];
    totalData: number;
    totalEventDUM: number;
    totalEventCasefile: number;
    dataMaps: Array<IMapCoordinate> | [];
    dataTimeLine: IDataTimeLine;
    timelineStatus: IStatusApi;
    status: IStatusApi;
    confirmedCount: string | number;
    statusCreate: IStatusApi;
  };
  selectedDetailDumGlobalIdIndex?: number;
  detailEventData: {
    data?: DumGlobalEventDetailsDto;
    status: IStatusApi;
  };
  dataMapsGrouped: IMapCoordinateGrouped[];
  mapReady: boolean;
  _api: DumQueryApi | undefined;
  overviews: OverviewType;
  dumEventAbortController: AbortController[];

  getDUMEvent: (_api: DumQueryApi) => void;
  setActiveTimeLine: (
    _api: DumQueryApi,
    _data: IItemTimeLine,
    cancelOther?: boolean
  ) => void;
  createEvent: (
    _api: DumCommandApi,
    _payload: IReqBodyEvent
  ) => Promise<boolean>;
  resetStatusCreate: () => void;
  getDetailEvent: (
    _id: string | number,
    _type: 'dum' | 'casefile' | 'riftstorm' | 'report' | undefined,
    _api: DumQueryApi
  ) => void;
  refetchData: () => void;
}

export class DUMEvents implements IDUMEventsStore {
  DUMEventDatas = {
    data: [] as IDumSorted[],
    totalData: 0,
    totalEventDUM: 0,
    totalEventCasefile: 0,
    dataMaps: [] as Array<IMapCoordinate>,
    dataTimeLine: defaultDataTimeLine as IDataTimeLine,
    timelineStatus: defaultStatusApi as IStatusApi,
    status: defaultStatusApi as IStatusApi,
    confirmedCount: 0,
    statusCreate: defaultStatusApi as IStatusApi
  };
  selectedDetailDumGlobalIdIndex?: number = undefined;
  detailEventData: {
    data?: DumGlobalEventDetailsDto;
    status: IStatusApi;
  } = {
    data: undefined,
    status: defaultStatusApi
  };
  dataMapsGrouped: IMapCoordinateGrouped[] = [];

  mapReady = false;
  overviews: OverviewType = [];
  rootStore: RootStore;
  _api: DumQueryApi | undefined = undefined;
  dumEventAbortController: AbortController[] = [];

  private source: CancelTokenSource | undefined = undefined;
  private cachedActiveTimelines: {
    data: IMapCoordinate[];
    totalData: number;
    totalEventDUM: number;
    totalEventCasefile: number;
    filters: {
      year: number;
      month: number;
    };
  }[] = [];

  constructor(rootStore: RootStore) {
    makeAutoObservable(this);
    this.rootStore = rootStore;
    if (axios && axios.CancelToken) {
      this.source = axios.CancelToken.source();
    }
  }

  async getDUMEvent(_api: DumQueryApi) {
    if (!this._api) {
      this._api = _api;
    }
    this.DUMEventDatas.status.isLoading = true;
    const anomalyRating =
      this.rootStore.AdvanceFilterStore.filters.anomalyRating?.every(
        (v) => v === -1
      )
        ? undefined
        : this.rootStore.AdvanceFilterStore.filters.anomalyRating?.filter(
            (v) => v !== undefined
          );
    const ecosystem =
      this.rootStore.AdvanceFilterStore.filters.ecosystem?.every(
        (v) => v === -1
      )
        ? undefined
        : this.rootStore.AdvanceFilterStore.filters.ecosystem?.filter(
            (v) => v !== undefined
          );

    try {
      const results = await _api.dumOverviewGet(
        this.rootStore.AdvanceFilterStore.filters.year.from,
        this.rootStore.AdvanceFilterStore.filters.year.end,
        this.rootStore.AdvanceFilterStore.filters.location,
        this.rootStore.AdvanceFilterStore.filters.contries?.filter(
          (v) => v !== undefined
        ),
        ecosystem,
        anomalyRating
      );
      if (results && results.status === 200 && results.data) {
        const data = results.data;
        this.overviews = data.data?.slider_data as OverviewType;
        this.rootStore.AdvanceFilterStore.setAllYears(this.overviews);
        this.rootStore.AllEventStore.setAllYears(this.overviews);
      }

      let listTimeLine: IDataTimeLine;
      if (this.overviews.length > 0) {
        const { newListData, sortedData } = await sortingFoundData(
          this.overviews
        );
        listTimeLine = FilterDataTimeLine(newListData);
        if (this.rootStore.AdvanceFilterStore.allYears.length === 0) {
          this.rootStore.AdvanceFilterStore.filters.year = {
            from: sortedData[0].year,
            end: sortedData[sortedData.length - 1].year
          };
        }
      } else {
        listTimeLine = {
          active: undefined,
          starYear: 0,
          endYear: 0,
          list: []
        };
        this.DUMEventDatas.dataMaps = [];
      }

      const newStatus = {
        isSuccess: true,
        isLoading: false,
        isError: false,
        message: ''
      };

      this.DUMEventDatas = {
        ...this.DUMEventDatas,
        dataTimeLine: listTimeLine,
        status: newStatus,
        totalEventDUM: 0,
        totalEventCasefile: 0
      };
      this.rootStore.AdvanceFilterStore.initialize = true;
    } catch (err) {
      this.DUMEventDatas = {
        ...this.DUMEventDatas,
        ...catchError(err)
      };
    }
  }

  private checkEventCachedData = (year: number, month: number) => {
    const findCachedData = this.cachedActiveTimelines.find(
      ({ filters }) => year === filters.year && month === filters.month
    );
    if (findCachedData) {
      return findCachedData;
    }
    return undefined;
  };

  private cancelDumFetching = (cancelling?: boolean) => {
    if (cancelling) {
      this.source?.cancel('another api is on its way');
      this.source = axios.CancelToken.source();
      this.dumEventAbortController.forEach((v) => {
        if (v) {
          v.abort();
        }
      });
    }
  };

  arrangingDataMaps = (data: DumDataHoverDto[]) => {
    let totalEventDUM = 0;
    let totalEventCasefile = 0;
    const dataMaps = data?.map((v) => {
      let dumName = null;
      let globalEventId = null;
      const hoverData: IDumGlobalEventDetailHoverData[] = [];
      for (const event of v.global_events ?? []) {
        let eventType: 'dum' | 'casefile' | 'riftstorm' | 'report' = 'dum';
        switch (event.product_type) {
          case 0:
            totalEventDUM++;
            break;
          case 1:
            eventType = 'riftstorm';
            break;
          case 2:
            eventType = 'casefile';
            totalEventCasefile++;
            break;
          case 3:
            eventType = 'report';
            totalEventDUM++;
            break;

          default:
            break;
        }
        dumName = event.name;
        globalEventId = event.id;

        hoverData.push({
          globalEventId,
          country: v.dum_location_name,
          latlon: [v.location?.latitude, v.location?.longitude],
          investigation: dumName,
          date: v.dum_date,
          description: '',
          author: event.author?.name,
          authorImageUrl:
            event.author?.image_url && event.author?.image_url !== 'url'
              ? event.author?.image_url
              : null,
          locationName: v.location?.location_name ?? 'Unknown location',
          reference: '',
          scaleOfEvent: 0,
          type: eventType,
          mediaUrl: ''
        });
      }
      return {
        city: v.location?.location_name,
        coordinates: [v.location?.latitude, v.location?.longitude],
        isMythicEvent: v.contain_mythic_event,
        id: v.id,
        hoverData
      } as IMapCoordinate;
    });

    return {
      dataMaps,
      totalEventCasefile,
      totalEventDUM
    };
  };

  async setActiveTimeLine(
    _api: DumQueryApi,
    _data: IItemTimeLine,
    cancelOther?: boolean
  ) {
    this.DUMEventDatas.dataTimeLine.active = _data;
    this.DUMEventDatas.timelineStatus.isLoading = false;
    this.DUMEventDatas.timelineStatus.isLoading = true;
    // get dum list data and update dataMaps
    try {
      const anomalyRating =
        this.rootStore.AdvanceFilterStore.filters.anomalyRating?.every(
          (v) => v === -1
        )
          ? undefined
          : this.rootStore.AdvanceFilterStore.filters.anomalyRating?.filter(
              (v) => v !== undefined
            );
      const ecosystem =
        this.rootStore.AdvanceFilterStore.filters.ecosystem?.every(
          (v) => v === -1
        )
          ? undefined
          : this.rootStore.AdvanceFilterStore.filters.ecosystem?.filter(
              (v) => v !== undefined
            );
      const countries =
        this.rootStore.AdvanceFilterStore.filters.contries?.filter(
          (v) => v !== undefined
        );
      const location = this.rootStore.AdvanceFilterStore.filters.location;

      // check on cached data first
      const cachedData = this.checkEventCachedData(_data.year, _data.month);
      if (cachedData) {
        this.DUMEventDatas = {
          ...this.DUMEventDatas,
          dataMaps: cachedData.data,
          totalData: cachedData.totalData,
          totalEventDUM: cachedData.totalEventDUM,
          totalEventCasefile: cachedData.totalEventCasefile,
          timelineStatus: {
            ...this.DUMEventDatas.timelineStatus,
            isLoading: false
          }
        };
        return;
      }
      this.cancelDumFetching(cancelOther);
      // if not cached then do fetch
      const resList = await _api.dumGet(
        _data.year,
        _data.month,
        undefined,
        undefined,
        location,
        countries,
        ecosystem,
        anomalyRating,
        '',
        {
          cancelToken: this.source?.token
        }
      );
      if (resList.status === 200) {
        const { dataMaps, totalEventCasefile, totalEventDUM } =
          this.arrangingDataMaps(resList.data.data ?? []);

        this.DUMEventDatas = {
          ...this.DUMEventDatas,
          dataMaps,
          totalData: totalEventDUM + totalEventCasefile,
          totalEventDUM,
          totalEventCasefile
        };

        this.cachedActiveTimelines.push({
          data: this.DUMEventDatas.dataMaps,
          filters: {
            year: _data.year,
            month: _data.month
          },
          totalData: this.DUMEventDatas.totalData,
          totalEventDUM: this.DUMEventDatas.totalEventDUM,
          totalEventCasefile: this.DUMEventDatas.totalEventCasefile
        });
        this.DUMEventDatas.timelineStatus.isLoading = false;
      }
    } catch (error) {
      this.DUMEventDatas.timelineStatus.isLoading = false;
    }
  }

  async createEvent(_api: DumCommandApi, _payload: IReqBodyEvent) {
    this.DUMEventDatas.statusCreate.isLoading = true;
    try {
      // Both routes are multipart/form-data and take flattened positional form
      // parameters. The two orders differ - `/dum/event` leads with the
      // coordinates, `/dum/event/{dumId}` has none - so they are spelled out
      // separately rather than shared.
      if (_payload.existingId) {
        await _api.dumEventDumIdPost(
          _payload.existingId,
          undefined,
          _payload.name,
          _payload.description,
          _payload.reference,
          _payload.eventDate,
          _payload.captchaToken ?? undefined,
          _payload.banner,
          _payload.bannerUrl
        );
      } else {
        await _api.dumEventPost(
          undefined,
          _payload.latitude,
          _payload.longitude,
          _payload.eventDate,
          _payload.name,
          _payload.description,
          _payload.reference,
          _payload.captchaToken ?? undefined,
          _payload.banner,
          _payload.bannerUrl
        );
      }
      const newStatus = {
        isSuccess: true,
        isLoading: false,
        isError: false,
        message: ``
      };

      this.DUMEventDatas.statusCreate = newStatus;
      return true;
    } catch (err) {
      let checkErr = 'Something went wrong';
      const serverMessage = readServerErrorMessage(err);
      if (serverMessage) {
        // The service explains banner rejections here; show its wording rather
        // than the generic transport error.
        checkErr = serverMessage;
      } else if (err instanceof Error) {
        checkErr = err.message ?? checkErr;
        if (`${err}`.includes('undefined')) {
          checkErr =
            'Error, Something went wrong. Please refresh or try again later';
        }
      }
      this.DUMEventDatas = {
        ...this.DUMEventDatas,
        statusCreate: {
          isSuccess: false,
          isLoading: false,
          isError: true,
          message: `${checkErr}`
        }
      };
      return false;
    }
  }

  async resetStatusCreate() {
    const newStatus = {
      ...defaultStatusApi,
      isLoading: false
    };

    this.DUMEventDatas.statusCreate = newStatus;
  }

  async getDetailEvent(
    _id: string | number,
    _type: 'dum' | 'casefile' | 'riftstorm' | 'report' | undefined,
    _api: DumQueryApi
  ) {
    this.detailEventData.status.isLoading = true;
    try {
      let checkData = null;
      const fetchApi = await _api.dumEventIdGet(_id.toString());
      checkData = fetchApi.data.data;
      const newStatus = {
        isSuccess: true,
        isLoading: false,
        isError: false,
        message: ``
      };
      this.detailEventData = {
        data: checkData as DumDTO,
        status: newStatus
      };
    } catch (err) {
      const newStatus = {
        isSuccess: false,
        isLoading: false,
        isError: true,
        message: `${err}`
      };
      this.detailEventData.status = newStatus;
    }
  }

  refetchData() {
    if (this._api) this.getDUMEvent(this._api);
  }
}

export const IDUMEventsDefault: IDUMEventsStore = {
  DUMEventDatas: {
    data: [],
    totalData: 0,
    totalEventDUM: 0,
    totalEventCasefile: 0,
    dataMaps: [],
    dataTimeLine: defaultDataTimeLine,
    timelineStatus: defaultStatusApi,
    status: defaultStatusApi,
    confirmedCount: 0,
    statusCreate: defaultStatusApi
  },
  detailEventData: {
    data: undefined,
    status: defaultStatusApi
  },
  dataMapsGrouped: [],
  mapReady: false,
  overviews: [],
  _api: undefined,
  dumEventAbortController: [],
  getDUMEvent: function (): void {
    //
  },
  setActiveTimeLine: function (): void {
    //
  },
  createEvent: function (): Promise<boolean> {
    //
    return Promise.resolve(false);
  },
  resetStatusCreate: function (): void {
    //
  },
  getDetailEvent: function (): void {
    //
  },
  refetchData: function (): void {
    //
  }
};
